import './SlideGenerator.scss';
import cn from 'classnames';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import { TemplateSummary, TemplateConfig } from '@blisspointmedia/bpm-types/dist/SlidesV2';
import { getChannelPrettyName } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Button } from '../../Components/Button';
import { Dropdown } from '../../Components/Dropdown';
import { FilterBar } from '../../Components/FilterBar';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { Page } from '../../Components/Page';
import { AlertDialog } from '../../Components/AlertDialog';
import { Appearance, Intent, Placement, Size, StretchMode } from '../../Components/designConstants';
import { awaitJSON, SlidesLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useSetError } from '../../redux/modals';
import { dateToMddyy } from '../../utils/date-utils';
import EmptyStateIcon from './EmptyState.svg?react';
import { NewDeckModal } from './NewDeckModal';
import { SlideEditor } from './SlideEditor';
import { SlideThumbnail } from './SlideThumbnail';
import { useSlideIdToSignedUrl } from './useSlideSignedUrl';

const SORT_OPTIONS = [
  { label: 'ABC', value: 'abc' },
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest First', value: 'oldest' }
];

export const SlideGenerator = () => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();
  const [isEditMode, setIsEditMode] = useState(false);
  const [allTemplates, setAllTemplates] = useState<TemplateSummary[] | undefined>();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateSummary | null>(null);
  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState<TemplateConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showNewDeckModal, setShowNewDeckModal] = useState(false);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].value);
  const [showDeleteConfigDialog, setShowDeleteConfigDialog] = useState(false);
  const [filterTemplates, setFilterTemplates] = useState<{
    filter: (line: TemplateSummary) => boolean;
  }>({
    filter: () => true
  });
  const isLoadingTemplatesRef = useRef(false);

  useEffect(() => {
    (async () => {
      if (isLoadingTemplatesRef.current) {
        return;
      }
      isLoadingTemplatesRef.current = true;
      setIsLoadingTemplates(true);
      try {
        const allTemplatesRes = await SlidesLambdaFetch('/getAllTemplates', {
          params: {
            company
          }
        });
        const data = await awaitJSON<TemplateSummary[]>(allTemplatesRes);
        setAllTemplates(data);
      } catch (e) {
        setError({
          message: `Failed to get slide templates. ERROR: ${e.message}`,
          reportError: e
        });
      } finally {
        isLoadingTemplatesRef.current = false;
        setIsLoadingTemplates(false);
      }
    })();
  }, [company, setError]);

  const { configs, blueprints } = useMemo(() => {
    if (!allTemplates) {
      return { configs: [], blueprints: [] };
    }
    return {
      configs: allTemplates.filter(row => row.configId),
      blueprints: allTemplates.filter(row => !row.configId)
    };
  }, [allTemplates]);

  const filteredAndSortedTemplates = useMemo(() => {
    const filtered = configs.filter(filterTemplates.filter);
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'abc') {
        const aName = a.configName || a.blueprintName;
        const bName = b.configName || b.blueprintName;
        return aName.localeCompare(bName);
      }
      const aMs = Date.parse(a.updatedAt);
      const bMs = Date.parse(b.updatedAt);
      if (sortBy === 'newest') {
        return bMs - aMs;
      }
      if (sortBy === 'oldest') {
        return aMs - bMs;
      }
      return 0;
    });
    return sorted;
  }, [configs, filterTemplates, sortBy]);

  // Auto-select first template only if no template is currently selected
  useEffect(() => {
    if (filteredAndSortedTemplates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(filteredAndSortedTemplates[0]);
    }
  }, [filteredAndSortedTemplates, selectedTemplate]);

  // Map of slideId to signed url of image for the selected template, fed to SlideEditor.
  // Shares the React Query cache with SlideThumbnail below, so slides already signed for the
  // grid resolve here with no extra network request.
  const slideIdToSignedUrl = useSlideIdToSignedUrl(selectedTemplate?.slides ?? []);

  const handleTemplateSelect = useCallback((template: TemplateSummary) => {
    setSelectedTemplate(template);
  }, []);

  const handleEditExistingPreset = useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }
    setLoadingConfig(true);
    const res = await SlidesLambdaFetch('/getTemplateConfig', {
      params: {
        company,
        blueprintId: selectedTemplate.blueprintId,
        configId: selectedTemplate.configId,
        testCompanyPageKey: 'SLIDES'
      }
    });
    const data = await awaitJSON<TemplateConfig>(res);
    setSelectedTemplateConfig(data);
    setLoadingConfig(false);
    setIsEditMode(true);
  }, [company, selectedTemplate]);

  const handleEditNewDeck = useCallback(
    async (blueprintId: string, templateSummary: TemplateSummary) => {
      setLoadingConfig(true);
      setSelectedTemplate(templateSummary);
      const res = await SlidesLambdaFetch('/getTemplateConfig', {
        params: {
          company,
          blueprintId,
          configId: null,
          testCompanyPageKey: 'SLIDES'
        }
      });
      const data = await awaitJSON<TemplateConfig>(res);
      setSelectedTemplateConfig(data);
      setLoadingConfig(false);
      setShowNewDeckModal(false);
      setIsEditMode(true);
    },
    [company]
  );

  const handleSaveConfigSuccess = useCallback(
    async (configId: string) => {
      try {
        // Refetch all templates to include the new preset
        setLoadingConfig(true);
        setIsLoadingTemplates(true);

        // Refetch all templates
        try {
          const myTemplates = await SlidesLambdaFetch('/getAllTemplates', {
            params: {
              company
            }
          });
          const allTemplatesData = await awaitJSON<TemplateSummary[]>(myTemplates);
          setAllTemplates(allTemplatesData);
        } catch (e) {
          setError({
            message: `Failed to get slide templates after saving config. ERROR: ${e.message}`,
            reportError: e
          });
        }

        // Refetch the current template config after save
        if (selectedTemplateConfig) {
          const configIdToUse = configId || selectedTemplateConfig.configId || null;
          const res = await SlidesLambdaFetch('/getTemplateConfig', {
            params: {
              company,
              blueprintId: selectedTemplateConfig.blueprintId,
              configId: configIdToUse,
              testCompanyPageKey: 'SLIDES'
            }
          });
          const data = await awaitJSON<TemplateConfig>(res);
          setSelectedTemplateConfig(data);
        }
      } catch (e) {
        setError({
          message: `Failed to refetch slide templates after saving config. ERROR: ${e.message}`,
          reportError: e
        });
      } finally {
        setLoadingConfig(false);
        setIsLoadingTemplates(false);
      }
    },
    [company, selectedTemplateConfig, setError]
  );

  const handleDeleteConfig = useCallback(async () => {
    try {
      if (!selectedTemplate) {
        return;
      }
      setDeletingConfig(true);
      await SlidesLambdaFetch('/deleteSlidesConfig', {
        method: 'DELETE',
        body: {
          configId: selectedTemplate.configId
        }
      });

      // Refetch templates
      const myTemplates = await SlidesLambdaFetch('/getAllTemplates', {
        params: {
          company
        }
      });
      const allTemplatesData = await awaitJSON<TemplateSummary[]>(myTemplates);
      setAllTemplates(allTemplatesData);
    } catch (e) {
      setError({
        message: `Failed to delete slide config. ERROR: ${e.message}`,
        reportError: e
      });
    } finally {
      setSelectedTemplate(null);
      setSelectedTemplateConfig(null);
      setDeletingConfig(false);
    }
  }, [selectedTemplate, company, setError]);

  return (
    <Page title='Slide Generator' pageType='Slide Generator' minWidth='500px'>
      <div className='build-slides' data-testid='slide-generator'>
        {company && isEditMode && selectedTemplateConfig ? (
          <SlideEditor
            template={selectedTemplateConfig}
            slideIdToSignedUrl={slideIdToSignedUrl}
            company={company}
            onClose={() => setIsEditMode(false)}
            onSaveConfigSuccess={handleSaveConfigSuccess}
          />
        ) : (
          <div className='build-slides-container'>
            <div className='build-slides-header'>
              <div className='title'>Deck Library</div>
              {configs.length > 0 && (
                <div className='build-slides-header-right'>
                  <Button
                    size={Size.LG}
                    data-testid='new-deck-button'
                    leadingElement={<MdAdd />}
                    onClick={() => setShowNewDeckModal(true)}
                  >
                    New Deck
                  </Button>
                </div>
              )}
            </div>
            {configs.length > 0 && (
              <div className='build-slides-search-filter'>
                <FilterBar
                  size={Size.LG}
                  options={['configName']}
                  lines={configs}
                  onFilter={filter => {
                    setFilterTemplates({ filter });
                  }}
                />
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  options={SORT_OPTIONS}
                  onChange={setSortBy}
                  label='Sort by'
                  value={sortBy}
                  stretch={StretchMode.FIT}
                />
              </div>
            )}

            {isLoadingTemplates && (
              <div className='build-slides-body empty' data-testid='loading-state'>
                <div className='empty-state'>
                  <div className='empty-title'>Loading templates...</div>
                </div>
              </div>
            )}
            {!isLoadingTemplates && configs.length === 0 && (
              <div className='build-slides-body empty' data-testid='empty-state'>
                <div className='empty-state'>
                  <EmptyStateIcon />
                  <div className='empty-title'>No custom decks yet.</div>
                  <div className='empty-sub-title'>Load a Blueprint to get started!</div>
                  <Button
                    size={Size.LG}
                    data-testid='new-deck-button-empty'
                    className='view-skeletons'
                    onClick={() => setShowNewDeckModal(true)}
                    leadingElement={<MdAdd />}
                  >
                    New Deck
                  </Button>
                </div>
              </div>
            )}
            {!isLoadingTemplates && configs.length > 0 && (
              <div className='build-slides-body'>
                <div className='build-slides-sidebar'>
                  <div className='sidebar-list' data-testid='templates-sidebar'>
                    {filteredAndSortedTemplates.map(t => (
                      <TemplateListItem
                        key={`${t.blueprintId}-${t.configId}`}
                        template={t}
                        isSelected={
                          selectedTemplate?.blueprintId === t.blueprintId &&
                          selectedTemplate?.configId === t.configId
                        }
                        onSelect={() => handleTemplateSelect(t)}
                      />
                    ))}
                  </div>
                </div>
                <div className='build-slides-main' data-testid='template-detail'>
                  <div className='panel-toolbar'>
                    <div className='config-title' data-testid='template-detail-title'>
                      {selectedTemplate?.configName || selectedTemplate?.blueprintName}
                    </div>
                    <div className='config-description'>{selectedTemplate?.description}</div>
                    <div>Blueprint: {selectedTemplate?.blueprintName}</div>
                    <div>Channel: {getChannelPrettyName(selectedTemplate?.channel || '')}</div>
                    <div>
                      Created on {dateToMddyy(String(selectedTemplate?.createdAt))} by{' '}
                      {selectedTemplate?.createdBy}
                    </div>
                    <div>
                      Last Modified {dateToMddyy(String(selectedTemplate?.updatedAt))} by{' '}
                      {selectedTemplate?.updatedBy}
                    </div>
                  </div>
                  <div className='slides-grid'>
                    {selectedTemplate?.slides
                      .slice()
                      .sort((a, b) => a.slideNumber - b.slideNumber)
                      .map(slide => (
                        <SlideThumbnail
                          key={slide.slideId}
                          slideNumber={slide.slideNumber}
                          imageUrl={slide.imageUrl}
                        />
                      ))}
                  </div>
                  <div className='bottom-controls'>
                    <Button
                      size={Size.LG}
                      data-testid='delete-template-button'
                      className='delete-button'
                      onClick={() => setShowDeleteConfigDialog(true)}
                      appearance={Appearance.EMPTY}
                      leadingElement={<MdDelete />}
                      disabled={deletingConfig}
                    >
                      {deletingConfig ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Button
                      size={Size.LG}
                      data-testid='edit-template-button'
                      className='edit-button'
                      onClick={handleEditExistingPreset}
                      appearance={Appearance.OUTLINED}
                      leadingElement={<MdEdit />}
                      disabled={loadingConfig}
                    >
                      {loadingConfig ? 'Loading...' : 'Edit'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <NewDeckModal
              show={showNewDeckModal}
              blueprints={blueprints}
              onHide={() => setShowNewDeckModal(false)}
              onSubmit={handleEditNewDeck}
            />
            <AlertDialog
              intent={Intent.ERROR}
              show={showDeleteConfigDialog}
              onHide={() => setShowDeleteConfigDialog(false)}
              title='Are you sure?'
              body='Are you sure you want to delete this deck? This action cannot be undone.'
              primaryAction={{
                onClick: () => {
                  setShowDeleteConfigDialog(false);
                  handleDeleteConfig();
                },
                label: 'Delete'
              }}
              secondaryAction={{ onClick: () => setShowDeleteConfigDialog(false) }}
              passthroughProps={{ 'data-testid': 'delete-config-dialog' }}
            />
          </div>
        )}
      </div>
    </Page>
  );
};

interface TemplateListItemProps {
  template: TemplateSummary;
  isSelected: boolean;
  onSelect: () => void;
}

const TemplateListItem = ({ template, isSelected, onSelect }: TemplateListItemProps) => (
  <InteractiveDiv
    data-testid='template-list-item'
    className={cn('build-slides-item', { selected: isSelected })}
    onClick={onSelect}
  >
    <div className='item-title'>{template.configName || template.blueprintName}</div>
    <div className='item-description'>{template.description}</div>
    <div className='item-length'>({template.slides?.length} Slides)</div>
    <div className='item-last-updated'>
      Last updated on {dateToMddyy(template.updatedAt)} by {template.updatedBy}
    </div>
  </InteractiveDiv>
);
